/* ════════════════════════════════════════════════════════════════════════
   schriftbot — DESIGN SYSTEM (dark + light, v2, 2026-10-01)
   Ported from the webtamo system (v3). Same architecture, same type
   scale, same shapes, same motion — inverted onto Schriftbot's own
   near-black canvas so the site still reads as the site people know.
   Import { brand } (alias: ds) + { sx } everywhere. Change values HERE.
   ────────────────────────────────────────────────────────────────────────

   ◆ WHAT CARRIED OVER FROM WEBTAMO, AND WHAT CHANGED

   COLOR — three colors plus functional grays. Nothing else.
     · Ink      #0B0C0F — the page, always. (webtamo: white)
     · White    #FFFFFF — ALL text. There is no half-opacity body text.
     · Blue     #0A84FF — PRIMARY BUTTONS ONLY. The only colored element
       on any screen is the thing you can press. This is the webtamo
       system's own Apple blue in its dark-mode variant, which is why it
       sits right in a ported system: same role, tuned for a dark canvas.
       It replaces the previous rose #F05F96. The peach→rose gradient and
       the blurred `accent-glow` bleed stay retired.
       Its label is #0B0C0F, not white — white on this blue is 3.7:1 and
       fails AA; near-black on it is 5.4:1 and passes. Every vivid accent
       that reads well on near-black wants a dark label, so the rule is
       the same as before and only the hue changed.
     · Grays are FILLS, never text: #141519 section cards / wells,
       #1C1E23 inputs & inner rows, #262A31 secondary buttons,
       #2A2D34 decorative icons.
     · Light is also a surface (confirmation screens): #0B0C0F on #F7F7F8.

   TYPE — two faces, FOUR variations total. Never invent a fifth.
     T1  Instrument Serif 400, clamp(40px, 5.6vw, 72px), lh 1.12,
         ls -0.01em — TITLES ONLY. Italic of the same face is allowed
         inside a title for emphasis. The serif never appears at body
         size and never carries UI.
     T2  Outfit 400, 18px, lh 1.65 — all body text, inputs, answers.
     T3  Outfit 600, 14px, lh 1.5 — labels, captions, nav, footer.
         NO uppercase. NO tracking-widest. This is the single biggest
         visible change: the old site set nearly every label in
         `uppercase tracking-widest font-black`, which is what made a
         paid product read like a fan page.
     T4  Outfit 500, 17px — buttons (15px in the small size).

   SHAPE — soft and generous.
     · Screens/sections: rounded cards, radius 28, separated by a 12px
       gutter against the page.
     · Inner surfaces (rows, wells, inputs): radius 18.
     · Buttons: full pills (radius 980). Blue bg, near-black text;
       hover = opacity .88; active = scale(.985). Heights 50 / 38.
     · NO shadows, NO gradients, NO decorative glows. Depth comes from
       the gray fills and the 12px gutter. Hairlines exist only where a
       fill cannot carry the edge (they are #1C1E23, never white/10).

   LAYOUT — one screen per thought.
     · Legal & marketing pages: content max-width 720 (reading) or 1140
       (grids), padding 96px vertical / clamp(22px, 5vw, 48px) horizontal.
     · The editor keeps its own shell; it consumes these tokens for
       type, radius, spacing and buttons.

   MOTION — few, soft, meaningful.
     · Entry: rise 16–20px + fade, .8s cubic-bezier(0.16,1,0.3,1),
       staggered ~.15s. Scroll reveals: same curve, threshold ~0.2.

   VOICE — plain words, no shouting, no fake urgency.
     Schriftbot turns typed text into handwriting you can print or share.
     It is sold as a general-purpose writing tool — notes, cards, letters,
     invitations, journals, mockups — and never as a way to pass work off
     as hand-written by someone else. Copy never targets "getting out of"
     schoolwork, and never implies the output will fool a marker.
     Canonical claims: monthly, incl. VAT · cancel any time before the
     next billing month · no hidden cost · the withdrawal waiver is an
     explicit, unticked choice, never a bundled one.
     NEVER: "cheat", "undetectable", "nobody will notice", fake scarcity,
     or any promise the product does not keep.
   ════════════════════════════════════════════════════════════════════════ */

/* THEMES — the values below are CSS variables. Dark is the default; light
   is `[data-theme="light"]` on <html> (src/theme.js sets it, src/index.css
   defines both palettes). Change a color in index.css, not here. */
export const brand = {
  /* ── COLOR ── */
  color: {
    // Themable tokens resolve to CSS variables (see src/index.css), so one
    // JS import serves both the dark and the light theme. They are plain
    // strings for style props and template literals — never parse them
    // (no hex maths) and never hand them to a <canvas>; use `white`,
    // `black` and the legacy literals below for that.

    // Page & surfaces
    canvas: "var(--sb-canvas)",   // the page — always
    surface: "var(--sb-surface)", // raised surfaces / cards on the canvas
    sunken: "var(--sb-surface)",  // section cards, wells, input rows
    soft: "var(--sb-soft)",       // inputs, inner rows, deeper insets
    raised: "var(--sb-raised)",   // hover state of a soft surface
    // Sekundärknöpfe brauchen eine eigene, deutlich abgesetzte Füllung:
    // die Eingabefeld-Farbe wäre als Knopf kaum sichtbar.
    control: "var(--sb-control)",
    controlHover: "var(--sb-control-hover)",
    iconGray: "var(--sb-icon-gray)", // decorative icons only — never text

    // Ink — body text is the full-contrast ink. `muted` is for captions
    // and metadata only; it is never used for anything the reader must
    // actually read.
    ink: "var(--sb-ink)",
    body: "var(--sb-ink)",
    muted: "var(--sb-muted)",
    subtle: "var(--sb-subtle)",

    // Hairlines — used only where a fill cannot carry the edge.
    line: "var(--sb-line)",
    lineStrong: "var(--sb-line-strong)",

    // Accent — PRIMARY BUTTONS ONLY. Dark: #0A84FF with a near-black
    // label (5.4:1). Light: #0071E3 (the logo blue) with a white label.
    accent: "var(--sb-accent)",
    accentInk: "var(--sb-accent-ink)",
    accentDeep: "var(--sb-accent-deep)", // hover companion (opacity .88)
    accentTint: "var(--sb-soft)",        // selected fills stay gray, not blue
    accentTintBorder: "var(--sb-line-strong)",

    // Light surface (confirmation / receipt screens) — light in BOTH
    // themes, so these stay literal.
    light: "#F7F7F8",
    lightDeep: "#FFFFFF",
    onLight: "#0B0C0F",
    onLightMuted: "rgba(11,12,15,0.66)",

    // Legacy tokens kept so no consumer breaks mid-migration. Literal
    // on purpose (canvas games draw with them).
    dark: "#0B0C0F",
    darkDeep: "#08090B",
    darkRaised: "#141519",
    onDark: "#FFFFFF",
    onDarkMuted: "rgba(255,255,255,0.72)",
    onDarkFaint: "rgba(255,255,255,0.52)",
    markNavy: "#2A2D34",
    markOlive: "#2A2D34",
    markRed: "#2A2D34",

    // Semantic (functional states only)
    success: "var(--sb-success)",
    successTint: "var(--sb-success-tint)",
    warning: "var(--sb-warning)",
    warningTint: "var(--sb-warning-tint)",
    danger: "var(--sb-danger)",
    dangerTint: "var(--sb-danger-tint)",

    // Real colors, not theme tokens: the handwriting editor paints its
    // paper and guide lines with these on a <canvas>.
    white: "#FFFFFF",
    black: "#0B0C0F",
  },

  /* ── TYPE — Outfit for everything; Instrument Serif for TITLES ONLY. ── */
  font: {
    display: "'Instrument Serif', Georgia, serif",           // titles (T1)
    body: "'Outfit', -apple-system, 'Helvetica Neue', sans-serif",
    serif: "'Instrument Serif', Georgia, serif",             // == display
  },
  text: {
    hero: "clamp(40px, 5.6vw, 72px)", // T1 on marketing screens
    h1: "clamp(32px, 4vw, 48px)",     // T1 on page headers
    h2: "clamp(26px, 3vw, 36px)",     // T1, small contexts
    h3: "22px",                        // T1, compact (modals, cards)
    lead: "18px",                      // == body; one body size
    body: "18px",                      // T2
    small: "14px",                     // T3
    tiny: "14px",                      // T3 — no smaller size exists
  },
  tracking: { tight: "-0.01em", snug: "-0.01em", normal: "0" },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 600 },
  leading: { tight: 1.12, snug: 1.2, normal: 1.65, relaxed: 1.65 },

  /* ── SPACING (4px base) ── */
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48, 16: 64, 20: 80, 24: 96 },

  /* ── RADIUS ── */
  radius: { sm: 12, md: 18, lg: 28, xl: 28, pill: 980 },

  /* ── BUTTON — blue pill, near-black text. The ONLY colored element. ── */
  button: {
    height: 50,
    smHeight: 38,
    padY: 15,
    padX: 30,
    fontSize: 17,
    smPadY: 9,
    smPadX: 20,
    smFontSize: 15,
    weight: 500,
    radius: 980,
  },

  /* ── SHADOW — none. Blur is the only depth effect, used sparingly. ── */
  shadow: { xs: "none", sm: "none", md: "none", lg: "none" },
  glass: {
    background: "var(--sb-glass)",
    panel: "var(--sb-glass-panel)",
    blur: "blur(16px)",
  },
  scrim: "var(--sb-scrim)",

  /* ── MOTION ── */
  ease: {
    out: "cubic-bezier(0.16, 1, 0.3, 1)",
    inOut: "cubic-bezier(0.65, 0, 0.35, 1)",
  },
  dur: { fast: "150ms", base: "220ms", slow: "700ms" },

  /* ── LAYOUT ── */
  container: 1140,
  reading: 720,        // legal / prose pages
  gutter: 12,          // the gap between section cards and page edge
  screenPad: "96px clamp(22px, 5vw, 48px)",
};

/* Backwards-compatible alias — some modules import { ds }. */
export const ds = brand;

/* Tiny helper: hex + alpha → rgba */
export const alpha = (hex, a) => {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

/* ── Reusable inline-style recipes — THE component designs.
   T1–T4 live here. Never restyle ad hoc in pages — change them here. ── */

const btnBase = (small) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: small ? 8 : 10,
  height: small ? brand.button.smHeight : brand.button.height,
  padding: small
    ? `0 ${brand.button.smPadX}px`
    : `0 ${brand.button.padX}px`,
  borderRadius: brand.button.radius,
  fontFamily: brand.font.body,
  fontSize: small ? brand.button.smFontSize : brand.button.fontSize,
  fontWeight: brand.button.weight,
  lineHeight: 1,
  letterSpacing: "-0.01em",
  textDecoration: "none",
  cursor: "pointer",
  border: "none",
  transition: `opacity ${brand.dur.base} ${brand.ease.out}, transform ${brand.dur.fast} ${brand.ease.out}, background ${brand.dur.base} ${brand.ease.out}`,
});

export const sx = {
  /* T1 — titles only. Instrument Serif 400. */
  display: (size = brand.text.h1) => ({
    fontFamily: brand.font.display,
    fontWeight: 400,
    fontSize: size,
    letterSpacing: brand.tracking.tight,
    lineHeight: brand.leading.tight,
    color: brand.color.ink,
    margin: 0,
  }),
  headline: {
    fontFamily: brand.font.display,
    fontWeight: 400,
    fontSize: brand.text.h1,
    letterSpacing: brand.tracking.tight,
    lineHeight: brand.leading.tight,
    color: brand.color.ink,
    margin: 0,
  },

  /* T3 — the label voice. Plain, no uppercase, no tracking. */
  sub: {
    fontFamily: brand.font.body,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: "0",
    textTransform: "none",
    color: brand.color.ink,
    margin: 0,
  },

  /* T2 — the one body style. */
  text: {
    fontFamily: brand.font.body,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.65,
    color: brand.color.ink,
    margin: 0,
  },

  /* T3 under the legacy "kicker" name — plain, no uppercase. */
  kicker: {
    fontFamily: brand.font.body,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: "0",
    textTransform: "none",
    color: brand.color.muted,
  },

  /* T3 in the muted voice — captions and metadata only. */
  caption: {
    fontFamily: brand.font.body,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    color: brand.color.muted,
    margin: 0,
  },

  /* T4 — buttons. Primary = rose pill (the only color on the screen). */
  btnPrimary: {
    ...btnBase(false),
    background: brand.color.accent,
    color: brand.color.accentInk,
  },
  btnPrimarySm: {
    ...btnBase(true),
    background: brand.color.accent,
    color: brand.color.accentInk,
  },
  /* Secondary = gray pill, white text. */
  btnSecondary: {
    ...btnBase(false),
    background: brand.color.control,
    color: brand.color.ink,
  },
  btnSecondarySm: {
    ...btnBase(true),
    background: brand.color.control,
    color: brand.color.ink,
  },
  btnGhostSm: {
    ...btnBase(true),
    background: "transparent",
    color: brand.color.muted,
  },
  btnDangerSm: {
    ...btnBase(true),
    background: brand.color.dangerTint,
    color: brand.color.danger,
  },

  /* The one card surface: gray well on the canvas, radius 28. */
  card: {
    background: brand.color.sunken,
    borderRadius: brand.radius.lg,
  },
  /* Inner surface: rows, wells, inputs. radius 18. */
  well: {
    background: brand.color.soft,
    borderRadius: brand.radius.md,
  },

  /* Form input — one style, everywhere. */
  input: {
    width: "100%",
    background: brand.color.soft,
    color: brand.color.ink,
    border: "none",
    outline: "none",
    borderRadius: brand.radius.md,
    padding: "14px 16px",
    fontFamily: brand.font.body,
    fontSize: 18,
    lineHeight: 1.65,
  },

  /* Legacy reading-text roles — resolve to T2 (no serif at body size). */
  lead: {
    fontFamily: brand.font.body,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.65,
    color: brand.color.ink,
    margin: 0,
  },
  bodyMuted: {
    fontFamily: brand.font.body,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.65,
    color: brand.color.muted,
    margin: 0,
  },
};

export default brand;
